import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Trash2, X, History, Plus, ChevronLeft, MessageSquare, RotateCcw, GitBranch,
  PanelRight, AppWindow, Settings, MessageCircle, Zap, AlertTriangle,
  LineChart, Star, Award, Tag, Gift, ClipboardList, Mail, Users, Clock,
  DollarSign, Briefcase, BarChart3, MessagesSquare, CalendarCheck, Calendar,
  FileText, CalendarDays, Share2, Send, Inbox, TrendingUp, MessageSquareReply,
  Globe, Brain, Monitor, LayoutDashboard, Building, Bell, Sparkles,
  type LucideIcon
} from 'lucide-react';
import { ButtonGroup } from '@/components/ui/button-group';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { cn } from '@/lib/core/utils';
import { SimpleTooltip } from '@/components/overlays/simple-tooltip';
import { AI_ASSISTANT } from '@/lib/ai-chat/constants';
import { SALES_AI_ASSISTANT } from '@/lib/sales-chat/constants';
import { AIMessageList } from './AIMessageList';
import { AIMessageInput, type AIMessageInputHandle } from './AIMessageInput';
import { useAIChat } from './useAIChat';
import { useSalesAIChat } from '../sales-chat/useSalesAIChat';
import { useAIView } from '@/context/AIViewContext';
import type { AIToolCall, AIMessage } from './types';

// Map icon names to Lucide icon components
const TAB_ICONS: Record<string, LucideIcon> = {
  'LineChart': LineChart,
  'Star': Star,
  'Award': Award,
  'Tag': Tag,
  'Gift': Gift,
  'ClipboardList': ClipboardList,
  'Mail': Mail,
  'Users': Users,
  'Clock': Clock,
  'DollarSign': DollarSign,
  'Briefcase': Briefcase,
  'BarChart3': BarChart3,
  'MessagesSquare': MessagesSquare,
  'CalendarCheck': CalendarCheck,
  'Calendar': Calendar,
  'FileText': FileText,
  'CalendarDays': CalendarDays,
  'Share2': Share2,
  'Send': Send,
  'Inbox': Inbox,
  'TrendingUp': TrendingUp,
  'MessageSquareReply': MessageSquareReply,
  'Globe': Globe,
  'Brain': Brain,
  'Monitor': Monitor,
  'LayoutDashboard': LayoutDashboard,
  'Building': Building,
  'Bell': Bell,
  'Sparkles': Sparkles,
  'Settings': Settings,
};

// Format relative time for conversation list
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export interface AIChatContentProps {
  /** Business ID - required for 'assistant' mode, ignored for 'sales' mode */
  businessId?: string | null;
  /** Visual variant */
  variant: 'sidebar' | 'floating';
  /** Mode: 'assistant' for dashboard (full features), 'sales' for landing page (minimal) */
  mode?: 'assistant' | 'sales';
  /** Initial message to send when component mounts (sales mode only) */
  initialMessage?: string | null;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
}

export const AIChatContent: React.FC<AIChatContentProps> = ({
  businessId,
  variant,
  mode = 'assistant',
  initialMessage,
  onClose,
  onToggleFullscreen,
}) => {
  const isSalesMode = mode === 'sales';

  // Auth/Business contexts - only used in assistant mode
  const authContext = !isSalesMode ? useAuth() : null;
  const businessContext = !isSalesMode ? useBusinessContext() : null;

  // AIView context - used in both modes for view switching
  const aiViewContext = useAIView();

  const session = authContext?.session;
  const selectedBusiness = businessContext?.selectedBusiness;
  const {
    pendingMessage = null,
    setPendingMessage = () => {},
    pendingHistoryView = false,
    setPendingHistoryView = () => {},
    pendingNewChat = false,
    setPendingNewChat = () => {},
    isBubbleCollapsed = false,
    setBubbleCollapsed = () => {},
    openSidebar = () => {},
    openFloating = () => {},
    viewMode = 'floating' as const,
  } = aiViewContext || {};

  // Use different hooks based on mode
  const assistantChat = !isSalesMode ? useAIChat(businessId || null, session?.access_token) : null;
  const salesChat = isSalesMode ? useSalesAIChat() : null;

  // Unified interface from either hook
  const {
    messages = [],
    isLoading = false,
    error = null,
    sendMessage = async () => {},
    stopGeneration = () => {},
    streamingContent = '',
  } = (isSalesMode ? salesChat : assistantChat) || {};

  // Sales mode: clear messages function
  const clearMessages = salesChat?.clearMessages;

  // Assistant-only features (with defaults for sales mode)
  const confirmTools = assistantChat?.confirmTools;
  const cancelPendingTools = assistantChat?.cancelPendingTools;
  const sendQuestionAnswers = assistantChat?.sendQuestionAnswers;
  const conversationSummaries = assistantChat?.conversationSummaries || [];
  const activeConversationId = assistantChat?.activeConversationId;
  const createNewConversation = assistantChat?.createNewConversation;
  const switchConversation = assistantChat?.switchConversation;
  const deleteConversation = assistantChat?.deleteConversation;
  const retryLastMessage = assistantChat?.retryLastMessage;
  const canRetry = assistantChat?.canRetry || false;
  const wasStopped = assistantChat?.wasStopped || false;
  const currentContext = assistantChat?.currentContext || { tabDisplayName: '', tabIconName: null };
  const resendMessage = assistantChat?.resendMessage;
  const salesResendMessage = salesChat?.resendMessage;
  const autoAcceptEnabled = assistantChat?.autoAcceptEnabled || false;
  const setAutoAcceptEnabled = assistantChat?.setAutoAcceptEnabled;

  // View state: 'chat' or 'history'
  const [view, setView] = useState<'chat' | 'history'>('chat');

  // Ref for the message input
  const inputRef = useRef<AIMessageInputHandle>(null);

  // Get current conversation name (assistant mode) or static title (sales mode)
  const currentConversation = conversationSummaries.find(c => c.id === activeConversationId);
  const conversationTitle = isSalesMode
    ? SALES_AI_ASSISTANT.name
    : (currentConversation?.title || 'New Chat');

  // Get assistant info based on mode
  const assistantInfo = isSalesMode ? SALES_AI_ASSISTANT : AI_ASSISTANT;

  // Handle pending message from floating bubble
  useEffect(() => {
    if (pendingMessage && !isLoading) {
      // If pendingNewChat is set, force a new conversation
      sendMessage(pendingMessage, undefined, pendingNewChat);
      setPendingMessage(null);
      if (pendingNewChat) {
        setPendingNewChat(false);
      }
    }
  }, [pendingMessage, isLoading, sendMessage, setPendingMessage, pendingNewChat, setPendingNewChat]);

  // Handle pending history view request from floating bubble
  useEffect(() => {
    if (pendingHistoryView) {
      setView('history');
      setPendingHistoryView(false);
    }
  }, [pendingHistoryView, setPendingHistoryView]);

  // Handle initial message (sales mode - sent from floating input bar)
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (isSalesMode && initialMessage && !initialMessageSentRef.current && !isLoading) {
      initialMessageSentRef.current = true;
      sendMessage(initialMessage);
    }
  }, [isSalesMode, initialMessage, isLoading, sendMessage]);

  // Handle selecting a conversation from history
  const handleSelectConversation = (id: string) => {
    switchConversation(id);
    setView('chat');
  };

  // Handle creating a new conversation
  const handleNewChat = () => {
    createNewConversation();
    setView('chat');
    // Focus the input after a brief delay to ensure the view has switched
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  // Handle deleting a conversation
  const handleDeleteConversation = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteConversation(id);
  };

  // Handle reverting a create operation
  const handleRevert = useCallback(async (toolCall: AIToolCall) => {
    const resourceId = toolCall.result?.shift?.id || toolCall.result?.employee?.id;
    const resourceType = toolCall.result?.shift ? 'shift' : 'employee';

    if (!resourceId) {
      console.error('Cannot revert: no resource ID found in tool result');
      return;
    }

    // Send a message to delete the resource
    await sendMessage(`Please delete the ${resourceType} with ID ${resourceId} that was just created.`);
  }, [sendMessage]);

  // Handle suggestion click from welcome screen
  const handleSuggestionClick = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  // Handle resend message
  const handleResend = useCallback((message: AIMessage) => {
    if (isSalesMode && salesResendMessage) {
      salesResendMessage(message);
    } else if (resendMessage) {
      resendMessage(message);
    }
  }, [isSalesMode, salesResendMessage, resendMessage]);

  // Handle edit message
  const handleEdit = useCallback((message: AIMessage, newContent: string) => {
    if (isSalesMode && salesResendMessage) {
      salesResendMessage(message, newContent);
    } else if (resendMessage) {
      resendMessage(message, newContent);
    }
  }, [isSalesMode, salesResendMessage, resendMessage]);

  const isFloating = variant === 'floating';

  return (
    <div className={cn(
      'flex flex-col bg-background h-full',
      isFloating ? 'rounded-lg' : ''
    )}>
      {/* Header */}
      <div className="border-b border-border bg-muted/30 shrink-0">
        {view === 'history' ? (
          <div className="flex items-center justify-between px-3 py-2.5">
            <div className="flex items-center gap-2">
              <SimpleTooltip content="Back to chat">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => setView('chat')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </SimpleTooltip>
              <span className="text-sm font-medium">Chat History</span>
            </div>
            <SimpleTooltip content="Close AI Assistant">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onClose}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </SimpleTooltip>
          </div>
        ) : (
          <div className={cn("px-3 py-2.5", !isSalesMode && "space-y-2")}>
            {/* Top row: Logo, title, and actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full flex items-center justify-center ring-1 ring-border shrink-0 bg-background">
                  <img
                    src="/media/tag-innovation-logo.png"
                    alt={assistantInfo.name}
                    className="h-4 w-4 rounded-sm"
                  />
                </div>
                <span className="text-sm font-medium truncate">{conversationTitle}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Assistant mode only: New chat, history */}
                {!isSalesMode && (
                  <ButtonGroup>
                    <SimpleTooltip content="New chat">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={handleNewChat}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </SimpleTooltip>
                    {conversationSummaries.length > 0 && (
                      <SimpleTooltip content="Chat history">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setView('history')}
                        >
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </SimpleTooltip>
                    )}
                  </ButtonGroup>
                )}
                {/* Sales mode only: Clear chat button */}
                {isSalesMode && messages.length > 0 && (
                  <SimpleTooltip content="Clear chat">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={clearMessages}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="sr-only">Clear chat</span>
                    </Button>
                  </SimpleTooltip>
                )}
                {/* Settings menu - view switching available in both modes */}
                <div className="relative group">
                  <SimpleTooltip content="Settings">
                    <div
                      className="h-7 w-7 border border-input rounded-md inline-flex items-center justify-center text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
                    >
                      <Settings className="h-3.5 w-3.5" />
                    </div>
                  </SimpleTooltip>
                  <div className="absolute right-0 top-full mt-1 w-48 p-1 bg-background border border-border rounded-md shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                    <ButtonGroup orientation="vertical" className="w-full">
                      <Button
                        variant={viewMode === 'sidebar' ? 'default' : 'outline'}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => openSidebar()}
                      >
                        <PanelRight className="h-4 w-4" />
                        Sidebar
                      </Button>
                      <Button
                        variant={viewMode === 'floating' ? 'default' : 'outline'}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => openFloating()}
                      >
                        <AppWindow className="h-4 w-4" />
                        Floating window
                      </Button>
                    </ButtonGroup>
                    <Separator className="my-1" />
                    <Button
                      variant={!isBubbleCollapsed ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setBubbleCollapsed(!isBubbleCollapsed)}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Show AI bubble
                    </Button>
                  </div>
                </div>
                {/* Assistant mode only: Auto-accept mode popover */}
                {!isSalesMode && (
                  <Popover>
                      <PopoverTrigger asChild>
                        <SimpleTooltip content="Auto-accept mode">
                        <div
                          className={cn(
                            "h-7 w-7 border rounded-md inline-flex items-center justify-center text-sm font-medium transition-colors cursor-pointer",
                            autoAcceptEnabled
                              ? "border-warning bg-warning/10 text-warning"
                              : "border-input hover:bg-accent hover:text-accent-foreground"
                          )}
                        >
                          <Zap className={cn("h-3.5 w-3.5", autoAcceptEnabled && "fill-current")} />
                        </div>
                        </SimpleTooltip>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-72 p-0">
                        <div className="p-3 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "p-1.5 rounded-md",
                              autoAcceptEnabled ? "bg-warning/20" : "bg-muted"
                            )}>
                              <Zap className={cn(
                                "h-4 w-4",
                                autoAcceptEnabled ? "text-amber-500 fill-amber-500" : "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">Auto-accept Mode</div>
                              <div className="text-xs text-muted-foreground">Skip confirmations</div>
                            </div>
                            <Switch
                              checked={autoAcceptEnabled}
                              onCheckedChange={setAutoAcceptEnabled}
                              className={autoAcceptEnabled ? "data-[state=checked]:bg-amber-500" : ""}
                            />
                          </div>
                          <Separator />
                          <div className={cn(
                            "p-2 rounded-md text-xs space-y-2",
                            autoAcceptEnabled
                              ? "bg-warning/10 border border-warning/30"
                              : "bg-muted/50"
                          )}>
                            <div className="flex items-start gap-2">
                              <AlertTriangle className={cn(
                                "h-4 w-4 shrink-0 mt-0.5",
                                autoAcceptEnabled ? "text-warning" : "text-muted-foreground"
                              )} />
                              <div>
                                <div className={cn(
                                  "font-medium",
                                  autoAcceptEnabled ? "text-warning" : "text-foreground"
                                )}>
                                  {autoAcceptEnabled ? "Auto-accept is ON" : "Use at your own risk"}
                                </div>
                                <div className="text-muted-foreground mt-1">
                                  {autoAcceptEnabled
                                    ? "AI actions will execute immediately without asking for confirmation."
                                    : "When enabled, the AI will create, update, and delete data without asking you first."
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                          {autoAcceptEnabled && (
                            <div className="flex items-center justify-center gap-1.5 text-xs text-warning">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-warning"></span>
                              </span>
                              Auto-accepting actions
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                )}
                {/* Close button - always shown */}
                <SimpleTooltip content="Close">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={onClose}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </SimpleTooltip>
              </div>
            </div>
            {/* Fork indicator - assistant mode only */}
            {!isSalesMode && currentConversation?.forkedFromConversationId && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 text-xs text-muted-foreground">
                <GitBranch className="h-3 w-3" />
                <span>Branched conversation</span>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs text-primary"
                  onClick={() => {
                    const originalId = currentConversation.forkedFromConversationId;
                    if (originalId && switchConversation) switchConversation(originalId);
                  }}
                >
                  View original
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* History View - assistant mode only */}
      {!isSalesMode && view === 'history' ? (
        <div className="flex-1 overflow-y-auto">
          {conversationSummaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
              <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm mb-3">No chat history yet</p>
              <Button
                variant="outline"
                onClick={handleNewChat}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                New Chat
              </Button>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {conversationSummaries.map((conv) => {
                const isActive = conv.id === activeConversationId;
                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    className={cn(
                      'group flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors',
                      'hover:bg-muted',
                      isActive && 'bg-primary/10 border-l-2 border-primary'
                    )}
                  >
                    {conv.forkedFromConversationId ? (
                      <GitBranch className={cn(
                        'h-4 w-4 shrink-0',
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      )} />
                    ) : (
                      <MessageSquare className={cn(
                        'h-4 w-4 shrink-0',
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      )} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        'text-sm truncate',
                        isActive ? 'font-medium' : ''
                      )}>{conv.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {conv.forkedFromConversationId && (
                          <span className="text-primary/70">Branch · </span>
                        )}
                        {conv.messageCount} messages · {formatRelativeTime(conv.updatedAt)}
                      </p>
                    </div>
                    <SimpleTooltip content="Delete conversation">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-30 hover:opacity-100 transition-opacity"
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                      >
                        <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </SimpleTooltip>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Chat View */
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3">
            <AIMessageList
              messages={messages}
              isLoading={isLoading}
              onRevert={isSalesMode ? undefined : handleRevert}
              onConfirmTools={isSalesMode ? undefined : confirmTools}
              onCancelTools={isSalesMode ? undefined : cancelPendingTools}
              onSuggestionClick={handleSuggestionClick}
              businessLogoUrl={isSalesMode ? null : selectedBusiness?.favicon_url}
              businessId={isSalesMode ? null : businessId}
              onQuestionsSubmit={isSalesMode ? undefined : sendQuestionAnswers}
              streamingContent={streamingContent}
              onResend={handleResend}
              onEdit={handleEdit}
              conversationId={isSalesMode ? null : activeConversationId}
              autoAcceptEnabled={isSalesMode ? false : autoAcceptEnabled}
              mode={mode}
            />
          </div>

          {/* Error display with retry button */}
          {(error || wasStopped) && (
            <div className="px-3 py-2 bg-destructive/10 text-destructive text-xs flex items-center justify-between gap-2 shrink-0">
              <span>{error || 'Generation stopped'}</span>
              {canRetry && retryLastMessage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={retryLastMessage}
                  className="h-6 px-2 text-xs"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          )}

          {/* Context pill - assistant mode only, positioned near input */}
          {!isSalesMode && currentContext.tabDisplayName && (
            <div className="flex items-center px-3 pb-1">
              <Badge variant="outline" className="gap-1.5 text-foreground">
                <span className="text-muted-foreground">In:</span>
                {currentContext.tabIconName && TAB_ICONS[currentContext.tabIconName] && (
                  (() => {
                    const IconComponent = TAB_ICONS[currentContext.tabIconName];
                    return <IconComponent className="h-4 w-4" />;
                  })()
                )}
                <span>{currentContext.tabDisplayName}</span>
              </Badge>
            </div>
          )}

          {/* Input */}
          <AIMessageInput
            ref={inputRef}
            onSend={sendMessage}
            onStop={stopGeneration}
            disabled={isLoading || (!isSalesMode && !businessId)}
            isLoading={isLoading}
            placeholder={
              isSalesMode
                ? 'Ask about TagInn...'
                : (businessId ? 'Ask Inny anything...' : 'Select a business first')
            }
            allowFileUpload={!isSalesMode && !!businessId}
          />
        </>
      )}
    </div>
  );
};

export default AIChatContent;
